import { useContext } from "react";
import { Controller, useForm } from "react-hook-form";
import { useModal } from "react-modal-hook";
import { isValidEmail } from "common/utils/strings";
import {
  useInviteUserMutation,
  UserAuthMethod,
  UserRoleName,
} from "../../generated/graphql";
import { useStatusToasts } from "../../hooks/useStatusToasts";
import { track } from "../../utils/tracking";
import { AuthContext } from "../Authorization/AuthContext";
import {
  ButtonSection,
  Container,
  ContentSection,
  FormSection,
  HeaderSection,
  InputRow,
  PrimaryButtons,
  SecondaryButtons,
} from "../Common/__styles__/Modal";
import { StyledLink } from "../Common/__styles__/Typography";
import { Button } from "../Common/Button";
import Modal from "../Common/Modal";
import { Text } from "../Inputs/react-hook-form";
import { ReactHookFormSelect } from "../Inputs/Select";
import { PiiPermissionsSelect, UserRoleSelect } from "./UserPermissionSelects";

interface NewUserInviteModalHook {
  onSubmit: () => void;
  onCancel: () => void;
}

export const useNewUserInviteModal = ({
  onSubmit,
  onCancel,
}: NewUserInviteModalHook) => {
  const [showNewUserInviteModal, hideNewUserInviteModal] = useModal(
    () => (
      <Modal onRequestClose={onCancel}>
        <NewUserInviteForm onSubmit={onSubmit} onCancel={onCancel} />
      </Modal>
    ),
    [onSubmit],
  );

  return [showNewUserInviteModal, hideNewUserInviteModal] as const;
};

type NewUserInviteStructure = {
  firstName: string;
  lastName: string;
  email: string;
  authMethod?: UserAuthMethod;
  roleName: UserRoleName;
  canViewPersonalIdentifiableInformation: boolean;
};

const AUTH_METHOD_OPTIONS = [
  { value: UserAuthMethod.PASSWORD, label: "Password" },
  { value: UserAuthMethod.SSO, label: "Single Sign On" },
];

const NewUserInviteForm = ({ onCancel, onSubmit }: NewUserInviteModalHook) => {
  const { addSuccessToast, addErrorToast } = useStatusToasts();
  const { account } = useContext(AuthContext);

  const {
    control,
    formState: { errors },
    getValues,
    handleSubmit,
    register,
  } = useForm<NewUserInviteStructure>({
    defaultValues: {
      roleName: "view_only",
      canViewPersonalIdentifiableInformation: false,
    },
  });

  const [newUserInvite, { loading }] = useInviteUserMutation({
    onCompleted: response => {
      const invite = response.inviteUser;

      addSuccessToast(
        `${invite?.firstName} ${invite?.lastName} was sent an invitation to create their account`,
      );

      const value = getValues();
      track("New user invited", {
        email: value.email,
        roleName: value.roleName,
      });

      onSubmit();
    },

    onError: error => {
      addErrorToast(error.message);
    },
  });

  const formOnSave = async (data: NewUserInviteStructure) => {
    if (loading) return;

    // Default to password auth method if SSO is not enabled
    const authMethod =
      account?.isSSOEnabled ? data.authMethod : UserAuthMethod.PASSWORD;

    await newUserInvite({
      variables: {
        ...data,
        authMethod,
      },
    });
  };

  return (
    <Container overflows>
      <HeaderSection>
        <h1>Create new user</h1>
        <h2>
          Enter the name and email address of the user you'd like to invite.
        </h2>
      </HeaderSection>
      <FormSection onSubmit={onCancel} overflows>
        <ContentSection withBottomDivider overflows>
          <InputRow>
            <Text
              label="First name"
              error={errors.firstName?.message}
              required
              {...register("firstName", {
                required: "First name is required",
              })}
            />
            <Text
              label="Last name"
              error={errors.lastName?.message}
              required
              {...register("lastName", {
                required: "Last name is required",
              })}
            />
          </InputRow>

          <InputRow>
            <Text
              label="Email"
              error={errors.email?.message}
              required
              {...register("email", {
                validate: val => {
                  if (!isValidEmail(val)) {
                    return "A valid email is required";
                  }
                  return;
                },
              })}
            />
          </InputRow>

          {account?.isSSOEnabled && (
            <InputRow>
              <ReactHookFormSelect
                control={control}
                name="authMethod"
                label="Authentication method"
                options={AUTH_METHOD_OPTIONS}
                error={errors.authMethod?.message}
                required
                rules={{
                  required: "Authentication method is required",
                }}
                placeholder="Select..."
              />
            </InputRow>
          )}
        </ContentSection>
        <ContentSection overflows>
          <Controller
            control={control}
            name="roleName"
            render={({ field }) => (
              <UserRoleSelect onChange={field.onChange} value={field.value} />
            )}
          />
          {account?.repetitiveLossEnabled && (
            <Controller
              control={control}
              name="canViewPersonalIdentifiableInformation"
              render={({ field }) => (
                <PiiPermissionsSelect
                  onChange={field.onChange}
                  value={field.value}
                />
              )}
            />
          )}
        </ContentSection>
        <ButtonSection>
          <SecondaryButtons>
            <StyledLink
              href="https://forerunner.helpscoutdocs.com/article/18-user-and-account-settings"
              target="_blank"
              onClick={() => track("User role documentation clicked")}
            >
              Learn more about our user roles
            </StyledLink>
          </SecondaryButtons>
          <PrimaryButtons>
            <Button
              onClick={onCancel}
              styleVariant="secondary"
              size="medium"
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit(formOnSave)}
              disabled={loading}
              size="medium"
              styleVariant="primary"
            >
              Save
            </Button>
          </PrimaryButtons>
        </ButtonSection>
      </FormSection>
    </Container>
  );
};

export default NewUserInviteForm;
